import React, { useState, useEffect, useCallback } from 'react';

import { 
  FeatureSource,
  ModelBase, 
  ReusableString
} from "@models";

import { 
  CharacterFeatureSource, 
  CharacterSheetInterface, 
  Creature, 
  CreatureInstance, 
  SummonInstance 
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

// import DisplayString from "@components/input/strings/DisplayString";
import { CircularProgress } from '@mui/material';
import Grid from "@mui/material/Grid2";
import DisplayStringWithReplacements from './DisplayStringWithReplacements';
import DandDAPI from '@dndUtilities/system_api';
import CheckBox from '@components/input/CheckBox';

const char_util = DandDCharacterUtilities.getInstance();
const api = DandDAPI.getInstance();

// ---------------------------------------------------------------------------
// Helpers — only split on "big" async replacers
// ---------------------------------------------------------------------------

/**
 * Finds the next BIG replacer: [Replacer:...] or {creature_stat_block.*} or {deity_table.*}
 * Returns { start, end } or null.
 * 
 * Small/synchronous replacers (e.g. {cfs.x}, {name}, {attack_string.*}, etc.)
 * are intentionally ignored here — they stay inline and are resolved as a unit
 * by getDisplayStringAsync so they never break surrounding HTML tags.
 */
const findNextBigReplacer = (s: string): { start: number; end: number } | null => {
  let earliest: { start: number; end: number } | null = null;

  const tryEarliest = (candidate: { start: number; end: number } | null) => {
    if (candidate && (earliest === null || candidate.start < earliest.start)) {
      earliest = candidate;
    }
  };

  // --- [Replacer:...] ---
  let index = s.indexOf("[Replacer:");
  if (index !== -1) {
    let depth = 0;
    let end = -1;
    for (let i = index; i < s.length; i++) {
      if (s[i] === "[") depth++;
      else if (s[i] === "]") {
        depth--;
        if (depth === 0) { end = i + 1; break; }
      }
    }
    if (end !== -1) tryEarliest({ start: index, end });
  }

  // --- {creature_stat_block.*} ---
  index = s.indexOf("{creature_stat_block.");
  if (index !== -1) {
    let depth = 0;
    let end = -1;
    for (let i = index; i < s.length; i++) {
      if (s[i] === "{") depth++;
      else if (s[i] === "}") {
        depth--;
        if (depth === 0) { end = i + 1; break; }
      }
    }
    if (end !== -1) tryEarliest({ start: index, end });
  }

  // --- {feature_source_desc.*} ---
  index = s.indexOf("{feature_source_desc.");
  if (index !== -1) {
    let depth = 0;
    let end = -1;
    for (let i = index; i < s.length; i++) {
      if (s[i] === "{") depth++;
      else if (s[i] === "}") {
        depth--;
        if (depth === 0) { end = i + 1; break; }
      }
    }
    if (end !== -1) tryEarliest({ start: index, end });
  }

  // --- {deity_table.*} ---
  index = s.indexOf("{deity_table.");
  if (index !== -1) {
    let depth = 0;
    let end = -1;
    for (let i = index; i < s.length; i++) {
      if (s[i] === "{") depth++;
      else if (s[i] === "}") {
        depth--;
        if (depth === 0) { end = i + 1; break; }
      }
    }
    if (end !== -1) tryEarliest({ start: index, end });
  }

  return earliest;
};

/**
 * Splits a string into tokens where each element is either:
 * - A big async replacer ([Replacer:...] or {creature_stat_block.*} or {deity_table.*})
 * - A plain segment (which may still contain small synchronous replacers —
 *   those are left intact and resolved by getDisplayStringAsync)
 */
const splitOnBigReplacers = (input: string): string[] => {
  const tokens: string[] = [];
  let remaining = input;

  while (remaining.length > 0) {
    const match = findNextBigReplacer(remaining);
    if (match === null) {
      tokens.push(remaining);
      break;
    }
    const { start, end } = match;
    if (start > 0) tokens.push(remaining.substring(0, start));
    tokens.push(remaining.substring(start, end));
    remaining = remaining.substring(end);
  }

  return tokens.filter(t => t.length > 0);
};

const isBigReplacer = (s: string): boolean =>
  s.startsWith("[Replacer:") || s.startsWith("{creature_stat_block.") || s.startsWith("{deity_table.");

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  display_string: string;
  cfs?: CharacterSheetInterface | CreatureInstance | SummonInstance | CharacterFeatureSource | Creature | FeatureSource;
  log_it?: boolean;
}

/**
 * Splits display_string ONLY on big async replacers ([Replacer:*] and
 * {creature_stat_block.*} and {deity_table.*}), 
 * so that HTML tags in the surrounding text are
 * never torn apart across sibling nodes.
 *
 * Small/synchronous replacers ({cfs.*}, {name}, {attack_string.*}, etc.) are
 * left in their containing segment and resolved all at once by
 * getDisplayStringAsync, which keeps them inside the same text node and
 * therefore inside the same HTML tag.
 *
 * Flow:
 *  • Multiple tokens → render each as its own recursive instance.
 *  • Single token, not a big replacer → resolve small replacers via
 *    getDisplayStringAsync and display the result with dangerouslySetInnerHTML
 *    (HTML from the server is intentional here).
 *  • Single token, is a big replacer → show spinner, resolve async, then
 *    recurse with the replacement string.
 */
export default function DisplayStringWithReplacementsRecursive({
  display_string,
  cfs,
  log_it = false,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show_raw_text, setShowRawText] = useState(false);
  const [arr, setArr] = useState<string[]>([]);
  const [replacement, setReplacement] = useState("");
  const [found_cfs, setFoundCFS] = useState<Creature | FeatureSource | null>(null);

  const load = useCallback(async () => {
    // debugger;
    const pieces = splitOnBigReplacers(display_string);

    if (log_it) {
      console.log("[DSWRR] input:", display_string);
      console.log("[DSWRR] pieces:", pieces);
    }

    if (pieces.length === 1 && isBigReplacer(pieces[0])) {
      // debugger;
      if (log_it && cfs instanceof Creature) {
        console.log(cfs.fbs[0].single_ff);
        const c = (api.temp_hash.creature.objects as Creature[]).find(o => o._id === cfs._id);
        if (c) {
          console.log(c.fbs[0].single_ff);
        }
      }
      // Single big async replacer — resolve it
      const res = await char_util.getDisplayStringAsync(display_string, cfs);
      if (log_it && cfs instanceof Creature) {
        console.log(cfs.fbs[0].single_ff);
        const c = (api.temp_hash.creature.objects as Creature[]).find(o => o._id === cfs._id);
        if (c) {
          console.log(c.fbs[0].single_ff);
        }
      }
      setReplacement(res.response);
      if (res.cfs instanceof Creature || res.cfs instanceof FeatureSource) {
        setFoundCFS(res.cfs);
      }
    } else {
      // Multiple tokens, or a single plain segment (possibly with small replacers).
      // Plain segments with small replacers are handled below in the render path.
      setArr(pieces);
    }

    setLoading(false);
  }, [display_string, cfs, log_it]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(load, 100);
    }
  }, [fetched, load]);

  // --- Loading ---
  if (!fetched || loading) {
    return <CircularProgress size={16} />;
  }
  
  const render = () => {
    console.log(arr);
    // --- Multiple tokens: recurse ---
    if (arr.length > 1) {
      return (
        <>
          {arr.map((str, key) => (
            <DisplayStringWithReplacementsRecursive
              key={key}
              display_string={str}
              cfs={cfs}
              log_it={log_it}
            />
          ))}
        </>
      );
    }
  
    // --- Big replacer resolved: recurse with replacement ---
    if (replacement.length > 0) {
      return (
        <DisplayStringWithReplacementsRecursive
          display_string={replacement}
          cfs={found_cfs ?? cfs}
          log_it={log_it}
        />
      );
    }
  
    // --- Single plain segment (may contain small synchronous replacers) ---
    // We resolve these inline via getDisplayStringAsync so the whole segment —
    // including any surrounding HTML tags — stays together as one unit.
    if (arr.length === 1 && !isBigReplacer(arr[0])) {
      return <PlainSegment segment={arr[0]} cfs={cfs} log_it={log_it} />;
    }
  
    return <></>;
  }

  if (api.user && api.user.admin) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <CheckBox 
            name="Show Raw Text"
            value={show_raw_text}
            onChange={setShowRawText}
          />
        </Grid>
        <Grid size={12}>
          { show_raw_text ?
            <>{display_string}</>
          : render() }
        </Grid>
      </Grid>
    );
  } else {
    return render();
  }
}

// ---------------------------------------------------------------------------
// PlainSegment — resolves small synchronous replacers in a leaf segment
// ---------------------------------------------------------------------------

interface PlainSegmentProps {
  segment: string;
  cfs?: CharacterSheetInterface | CreatureInstance | SummonInstance | CharacterFeatureSource | Creature | FeatureSource;
  log_it?: boolean;
}

/**
 * Handles a segment that contains no big async replacers.
 * 
 * If the segment contains any small replacers it calls getDisplayStringAsync
 * to resolve them all at once, then renders the result via
 * dangerouslySetInnerHTML so HTML in the resolved string is honoured.
 * 
 * If the segment is pure plain text (no replacers at all) it delegates to
 * DisplayString as before.
 */
function PlainSegment({ segment, cfs, log_it }: PlainSegmentProps) {
  const [resolved, setResolved] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [prev_segment, setPrevSegment] = useState(segment);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (segment !== prev_segment) {
      setPrevSegment(segment);
      setReloading(true);
    }
  }, [segment]);

  // A lightweight check: does the segment contain ANY replacer syntax?
  const hasAnyReplacer = (s: string) =>
    s.includes("{") || s.includes("[Replacer:");

  const load = useCallback(async () => {
    if (hasAnyReplacer(segment)) {
      if (log_it) {
        debugger;
      }
      const res = await char_util.getDisplayStringAsync(segment, cfs);
      setResolved(res.response);
    } else {
      setResolved(null); // signal: use DisplayString (plain text, no HTML needed)
    }
    setLoading(false);
  }, [segment, cfs]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      if (hasAnyReplacer(segment)) {
        setLoading(true);
        setTimeout(load, 100);
      }
    }
  }, [fetched, load, segment]);

  if (loading || reloading || (!fetched && hasAnyReplacer(segment))) {
    return <CircularProgress size={16} />;
  }

  if (log_it) {
    console.log(cfs);
    console.log(resolved);
    console.log(segment);
  }
  if (resolved !== null) {
    // Use a span with dangerouslySetInnerHTML so HTML in the resolved content
    // renders correctly (e.g. <strong>, <em>, table fragments, etc.)
    // return <span dangerouslySetInnerHTML={{ __html: resolved }} />;
    return <DisplayStringWithReplacements display_string={resolved} cfs={cfs} log_it={log_it} />;
  }

  return <DisplayStringWithReplacements display_string={segment} cfs={cfs} log_it={log_it} />;
}